import React, { useContext } from 'react';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { usePopoverOrTooltip } from './usePopoverOrTooltip';
import {
  NotificationType,
  NotificationRenderFunction,
} from '../../../Notifications/redux/types';
import { useCustomNotifications } from '../../../Notifications/CustomNotificationContext';
import { canShowTooltip } from './helpers';
import NotificationPopover from './NotificationPopover';
import TooltipPopover from './TooltipPopover';
import PopoverPopover from './PopoverPopover';
import type {
  OffsetModifier,
  PopperPlacement,
  ReferenceChildrenProps,
} from '@wix/da-ds/pkg/popper/types';
import { convertFromPopperPlacement } from '@wix/da-ds/pkg/popper/helpers';
import noop from '../../../utils/noop';

export interface Props {
  placement?: PopperPlacement;
  allowMobile?: boolean;
  isDisabled?: boolean;
  tooltip?: string;
  tooltipOffset?: OffsetModifier;
  notificationTypes?: (string | NotificationType)[];
  wrapperClassName?: string;
  arrowClassName?: string;
  contentClassName?: string;
  referenceClassName?: string;
  /** Force showing the tooltip regardless of hover/click */
  forceVisibleTooltip?: boolean;
  biData?: BiEvent;
  openBiData?: BiEvent;
  closeBiData?: BiEvent;
  customNotificationRender?: NotificationRenderFunction;
  renderPopover?: (props: { closeTooltip?: () => void }) => React.ReactNode;

  /**
   * ONLY USE THIS IF YOU KNOW WHAT YOU ARE DOING
   * By default, children passed to this component will be the reference element for the popper,
   * or alternatively you can pass a renderReference prop. This is helpful if you need to make the
   * reference element be some inner node on the entire structure of the tooltip.
   */
  renderReference?: (props: ReferenceChildrenProps) => React.ReactNode;
}

const PopoverWithTooltip: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  tooltip,
  tooltipOffset,
  renderPopover,
  wrapperClassName,
  referenceClassName,
  allowMobile = false,
  notificationTypes,
  isDisabled,
  customNotificationRender,
  arrowClassName,
  contentClassName,
  placement,
  forceVisibleTooltip,
  renderReference,
  biData,
  openBiData = biData,
  closeBiData = biData,
}) => {
  const { notifications } = useCustomNotifications(notificationTypes);
  const hasNotification = !!notificationTypes && notifications.length > 0;
  const isMobile = useContext(MobileContext);
  const { showTooltip, onClose, onMouseEnter, onMouseLeave, onReferenceClick } =
    usePopoverOrTooltip(
      !hasNotification && !!renderPopover,
      !hasNotification && !!tooltip,
      openBiData,
      closeBiData
    );

  const isActive =
    !isDisabled &&
    canShowTooltip({
      hasNotification,
      isMobile,
      allowMobile,
      hasChildren: !!children,
      hasPopover: !!renderPopover,
      hasTooltip: !!tooltip,
    });

  const { placement: primaryPlacement, secondaryPlacement } =
    convertFromPopperPlacement(placement);

  const isShowingTooltip = forceVisibleTooltip || showTooltip === 'tooltip';
  const noPopoverOrTooltip =
    !hasNotification && (!isActive || (!showTooltip && !forceVisibleTooltip));

  return (
    <div
      className={wrapperClassName}
      {...(!forceVisibleTooltip && {
        onMouseEnter,
        onMouseLeave,
      })}
    >
      {hasNotification && (
        <NotificationPopover
          arrowClassName={arrowClassName}
          contentClassName={contentClassName}
          referenceClassName={referenceClassName}
          tooltipOffset={tooltipOffset}
          notificationTypes={notificationTypes}
          customNotificationRender={customNotificationRender}
          onReferenceClick={onReferenceClick}
          renderReference={renderReference}
        >
          {children}
        </NotificationPopover>
      )}
      {!hasNotification && isActive && isShowingTooltip && (
        <TooltipPopover
          tooltip={tooltip}
          arrowClassName={arrowClassName}
          contentClassName={contentClassName}
          referenceClassName={referenceClassName}
          tooltipOffset={tooltipOffset}
          onReferenceClick={onReferenceClick}
          renderReference={renderReference}
        >
          {children}
        </TooltipPopover>
      )}
      {!hasNotification && isActive && showTooltip === 'popover' && (
        <PopoverPopover
          placement={primaryPlacement}
          secondaryPlacement={secondaryPlacement}
          arrowClassName={arrowClassName}
          contentClassName={contentClassName}
          referenceClassName={referenceClassName}
          tooltipOffset={tooltipOffset}
          onReferenceClick={onReferenceClick}
          renderPopover={renderPopover}
          onClose={onClose}
          renderReference={renderReference}
        >
          {children}
        </PopoverPopover>
      )}
      {noPopoverOrTooltip && (
        <div
          onClick={onReferenceClick}
          className={referenceClassName}
          style={referenceClassName ? {} : { display: 'inline-block' }}
        >
          {renderReference ? renderReference({ ref: noop }) : children}
        </div>
      )}
    </div>
  );
};
PopoverWithTooltip.displayName = 'PopoverWithTooltip';

export default PopoverWithTooltip;
